import { Box, Typography, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAppSelector } from "src/redux/store";
import { useNavigate } from "react-router-dom";
import { isAccessLocked, ACCESS_UNLOCK_LABEL } from "src/utils/accessLock";

const MainBody = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userInfo = useAppSelector((state) => state.root.user.data);
  const navigate = useNavigate();

  // Non-PSU GCA entities are locked out until the deadline: keep them on the
  // home page but disable the selection buttons. PSU entities are unaffected.
  const locked = isAccessLocked({
    isGCAEntity: userInfo?.isGCAEntity,
    gcaType: userInfo?.gcaType,
  });

  const handleNewSelectionClick = () => {
    window.localStorage.setItem("new_selection", "1");
    navigate("/selected");
  };

  const handleOldSelectionClick = () => {
    window.localStorage.setItem("new_selection", "0");
    navigate("/");
  };

  const selectionButtonSx = {
    backgroundColor: locked ? "#9CA3AF" : "#0D9488",
    color: "white",
    fontWeight: "bold",
    textTransform: "none",
    paddingX: 3,
    paddingY: 2,
    borderRadius: "12px",
    fontSize: isMobile ? "0.9rem" : "1rem",
    width: isMobile ? "100%" : "auto",
    "&:hover": {
      transform: locked ? "none" : "scale(1.03)",
    },
    "&.Mui-disabled": {
      backgroundColor: "#9CA3AF",
      color: "#E5E7EB",
    },
    transition: "all 0.2s ease-in-out",
  } as const;
  return (
    <Box
      sx={{
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        paddingTop: isMobile ? 8 : 12,
        paddingX: 3,
      }}
    >
      {/* Headings */}
      <Typography
        variant={isMobile ? "h5" : "h4"}
        fontWeight="bold"
        color="text.primary"
        sx={{ mb: 1, color: "black", textTransform: "uppercase" }}
      >
        {userInfo?.firstName || ""}
      </Typography>

      <Typography
        variant={isMobile ? "body1" : "h4"}
        fontWeight="semibold"
        color="#0D9488"
        sx={{ mb: 5 }}
      >
        Welcome to the Green Credit Programme Portal
      </Typography>

      {/* Buttons Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 3,
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: 800,
        }}
      >
        {/* ✅ Conditional Rendering based on userInfo.isNew */}
        {userInfo?.isNew ? (
          <>
            <Button
              variant="contained"
              disabled={locked}
              onClick={() => handleOldSelectionClick()}
              sx={selectionButtonSx}
            >
              View the details of ecorestoration blocks selected before
              30.08.2025
            </Button>
            <Button
              variant="contained"
              disabled={locked}
              onClick={() => handleNewSelectionClick()}
              sx={selectionButtonSx}
            >
              Select the eco-restoration blocks under the revised modalities
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            disabled={locked}
            onClick={() => handleNewSelectionClick()}
            sx={selectionButtonSx}
          >
            Select the eco-restoration blocks under the revised modalities
          </Button>
        )}
      </Box>

      {locked && (
        <Typography
          variant="body2"
          sx={{ fontSize: 20, mt: 3, fontWeight: 600, color: "red" }}
        >
          This is to inform you that you can access your GCP account from
          Monday, 8 June 2026 at 10:00 am (IST).
        </Typography>
      )}
    </Box>
  );
};

export default MainBody;
